import { type ReactNode } from "react";
import classNames from "classnames";
import { currencyFormatter } from "@common/helpers/currency";
import { SchedulePolicyPeriodCopy } from "@common/helpers/domains/accounting/schedulePolicyKind";
import { SchedulePreviewTable } from "./SchedulePreviewTable";
import { PreviewLadderRow } from "./schedulePreviewHelpers";
import styles from "./ScheduleDrawer.module.scss";

const PREVIEW_COLUMNS = [
    { id: "period", header: "Period", minWidth: 92, maxWidth: 100 },
    { id: "description", header: "Method", minWidth: 180, maxWidth: 200 },
    {
        id: "recognized",
        header: "Recognized",
        isNumeric: true,
        minWidth: 104,
        maxWidth: 112,
        headerTooltip: "Posted to the recognition account",
    },
    {
        id: "balance",
        header: "Balance",
        isNumeric: true,
        minWidth: 92,
        maxWidth: 100,
        headerTooltip: "Balance in the holding account",
    },
];

const MILESTONE_PREVIEW_COLUMNS = [
    { id: "period", header: "Expected date", minWidth: 104, maxWidth: 120 },
    {
        id: "milestone",
        header: "Milestone",
        isNumeric: true,
        minWidth: 80,
        maxWidth: 88,
    },
    { id: "description", header: "Description", minWidth: 140, maxWidth: 200 },
    {
        id: "recognized",
        header: "Recognition",
        isNumeric: true,
        minWidth: 104,
        maxWidth: 112,
        headerTooltip: "Posted to the recognition account",
    },
    PREVIEW_COLUMNS[3],
];

function withPeriodCopy<TColumn extends { id: string }>(
    columns: TColumn[],
    periodCopy: SchedulePolicyPeriodCopy | undefined,
): TColumn[] {
    if (periodCopy === undefined) {
        return columns;
    }
    return columns.map((column) =>
        column.id === "recognized"
            ? {
                  ...column,
                  header: periodCopy.label,
                  headerTooltip: periodCopy.tooltip,
              }
            : column,
    );
}

/** The Recognition preview's period ladder, with a running balance. */
export const SchedulePreviewLadderTable = ({
    ladder,
    periodCopy,
}: {
    ladder: PreviewLadderRow[];
    periodCopy?: SchedulePolicyPeriodCopy;
}) => {
    const isMilestoneLadder = ladder.some((row) => row.milestone != null);
    return (
        <SchedulePreviewTable
            className={styles.previewLadderTable}
            columns={withPeriodCopy(
                isMilestoneLadder ? MILESTONE_PREVIEW_COLUMNS : PREVIEW_COLUMNS,
                periodCopy,
            )}
            rows={ladder.map((row) => ({
                id: row.rowKey,
                values: {
                    period: row.periodLabel,
                    milestone: row.milestone
                        ? String(row.milestone.number)
                        : "",
                    description: row.description,
                    recognized: currencyFormatter.format(row.recognized),
                    balance: currencyFormatter.format(row.balance),
                },
            }))}
        />
    );
};

/** The Recognition preview pane: its header, then the ladder or a notice. */
export const SchedulePreviewPaneFrame = ({
    children,
}: {
    children: ReactNode;
}) => (
    <div className={styles.previewPane}>
        <div className={styles.previewHeader}>
            <h2 className={styles.previewTitle}>Recognition preview</h2>
        </div>
        {children}
    </div>
);

export const SchedulePreviewLadderResult = ({
    unavailableMessage,
    errorMessage,
    isLoading,
    ladder,
    periodCopy,
}: {
    /** Why there is no preview yet; null once one is requested. */
    unavailableMessage: string | null;
    errorMessage: string | null;
    isLoading: boolean;
    ladder: PreviewLadderRow[];
    periodCopy?: SchedulePolicyPeriodCopy;
}) => {
    if (unavailableMessage !== null) {
        return (
            <SchedulePreviewNotice>{unavailableMessage}</SchedulePreviewNotice>
        );
    }
    if (errorMessage !== null) {
        return <SchedulePreviewNotice>{errorMessage}</SchedulePreviewNotice>;
    }
    if (isLoading) {
        return <SchedulePreviewNotice>Loading preview…</SchedulePreviewNotice>;
    }
    return (
        <SchedulePreviewLadderTable ladder={ladder} periodCopy={periodCopy} />
    );
};

/** A line in place of the ladder: why it is missing, or that it is loading. */
export const SchedulePreviewNotice = ({
    children,
}: {
    children: ReactNode;
}) => (
    <p className={classNames(styles.helperText, styles.previewNotice)}>
        {children}
    </p>
);
